// The fake api's HQ online payments (F-PAY-05, F-HQ-13), past the HQ guard in `fake-server.ts` (`hq:payments`):
//
//   GET  /v1/hq/organizations/{o}/payments                  getHqOrganizationPayments   the provider, the commission, the payee
//   PUT  /v1/hq/organizations/{o}/payments/payee            saveHqOrganizationPayee     onboard or correct (status pending)
//   POST /v1/hq/organizations/{o}/payments/payee/refresh    refreshHqOrganizationPayee  ask the provider (the status moves on)
//
// The commission is the organisation setting `payments.commissionBps`, read through `commissionBps()` so the settings
// fake stays its only home. Bodies are checked with the contract's own schema, and the payee comes back masked: the
// last four of the PAN and the account number, never the numbers.

import type { Payee, Problem } from '@clavian/api-client';
import { SavePayeeSchema } from '@clavian/contracts';

import type { FakeResponse } from './fake-server';

const NOW = '2026-09-26T14:00:00.000Z';
const ROUTE = /^\/v1\/hq\/organizations\/([^/]+)\/payments(\/payee(\/refresh)?)?$/;

interface Helpers {
  readonly json: (status: number, body: unknown, headers?: Record<string, string>) => FakeResponse;
  readonly problem: (status: number, code: string, title: string, extra?: Partial<Problem>) => FakeResponse;
}

export function createFakeHqPayments(commissionBps: (organizationId: string) => number) {
  const state = {
    /** The deployment's provider; null answers as a platform with none configured. */
    provider: 'cashfree' as string | null,
    settlement: 'split' as 'split' | 'merchant' | 'none',
    payees: new Map<string, Payee>(),
    /** What the provider says the next time it is asked. */
    nextStatus: 'active' as Payee['status'],
    /** The provider refuses a save with this reason (400), or cannot be reached (503). */
    refuse: undefined as string | undefined,
    unreachable: false,
    /** Every save body, as sent. */
    saves: [] as unknown[],
    refreshes: 0,
  };

  const seed = (organizationId: string, overrides: Partial<Payee> = {}): Payee => {
    const payee: Payee = {
      provider: 'cashfree',
      providerAccountId: `clv_${organizationId.slice(-12)}`,
      status: 'active',
      businessName: 'Playground Gaming',
      businessType: 'proprietorship',
      panLast4: '234F',
      gstin: null,
      contactName: 'Anika Rao',
      contactEmail: 'anika@playground.example',
      contactPhone: '9876543210',
      bankAccountHolder: 'Playground Gaming',
      bankAccountLast4: '1234',
      bankIfsc: 'HDFC0001234',
      statusCheckedAt: '2026-09-26T12:00:00.000Z',
      updatedAt: NOW,
      ...overrides,
    };
    state.payees.set(organizationId, payee);
    return payee;
  };

  function handle(helpers: Helpers, method: string, pathname: string, input: Record<string, unknown>, allowed: ReadonlySet<string>): FakeResponse | undefined {
    const match = ROUTE.exec(pathname);
    if (match === null) return undefined;
    const { json, problem } = helpers;
    if (!allowed.has('hq:payments')) return problem(403, 'forbidden', 'Not allowed');
    const organizationId = match[1] as string;
    const payee = match[2];
    const refresh = match[3] !== undefined;

    if (payee === undefined) {
      if (method !== 'GET') return problem(405, 'method_not_allowed', 'Method not allowed');
      return json(200, {
        provider: state.provider,
        settlement: state.provider === null ? 'none' : state.settlement,
        commissionBps: commissionBps(organizationId),
        payee: state.payees.get(organizationId) ?? null,
      });
    }

    if (state.provider === null || state.settlement !== 'split') {
      return problem(409, 'online_payments_unavailable', 'Online payments are not available', {
        detail: 'No payment provider is configured, or it does not pay through payees.',
      });
    }

    if (refresh) {
      if (method !== 'POST') return problem(405, 'method_not_allowed', 'Method not allowed');
      const current = state.payees.get(organizationId);
      if (current === undefined) return problem(404, 'not_found', 'No payee yet');
      if (state.unreachable) return problem(503, 'payment_provider_unavailable', 'The payment provider could not be reached');
      state.refreshes += 1;
      return json(200, seed(organizationId, { ...current, status: state.nextStatus, statusCheckedAt: NOW }));
    }

    if (method !== 'PUT') return problem(405, 'method_not_allowed', 'Method not allowed');
    const parsed = SavePayeeSchema.safeParse(input);
    if (!parsed.success) {
      return problem(400, 'validation_failed', 'Validation failed', {
        errors: parsed.error.issues.map((issue) => ({ field: issue.path.map(String).join('.') || '(request)', code: issue.code, message: issue.message })),
      });
    }
    state.saves.push(input);
    if (state.unreachable) return problem(503, 'payment_provider_unavailable', 'The payment provider could not be reached');
    if (state.refuse !== undefined) return problem(400, 'validation_failed', 'Validation failed', { detail: state.refuse });
    const body = parsed.data;
    return json(
      200,
      seed(organizationId, {
        status: 'pending',
        businessName: body.businessName,
        businessType: body.businessType,
        panLast4: body.pan.slice(-4).toUpperCase(),
        gstin: body.gstin === undefined ? null : body.gstin.toUpperCase(),
        contactName: body.contactName,
        contactEmail: body.contactEmail,
        contactPhone: body.contactPhone,
        bankAccountHolder: body.bankAccountHolder,
        bankAccountLast4: body.bankAccountNumber.slice(-4),
        bankIfsc: body.bankIfsc.toUpperCase(),
        statusCheckedAt: null,
      }),
    );
  }

  return { state, seed, handle };
}

export type FakeHqPayments = ReturnType<typeof createFakeHqPayments>;
